import { api, queryString, type Items, type Paginated } from '@sentinel/web-core';
import type { LinkedIdentity, OrganizationUser, PermissionKey, Role } from '@sentinel/shared';

export interface PermissionDefinition {
  key: PermissionKey;
  resource: string;
  action: string;
  kind: 'matrix' | 'sensitive';
  moduleKey: string;
}

export const roleEndpoints = {
  list: () => api<Items<Role>>('/roles'),
  permissions: () => api<Items<PermissionDefinition>>('/permissions'),
  create: (body: {
    name: string;
    description: string;
    scope: string;
    inheritsRoleId?: string | null;
  }) => api<Role>('/roles', { method: 'POST', body }),
  duplicate: (id: string) => api<Role>(`/roles/${id}/duplicate`, { method: 'POST' }),
  update: (id: string, body: { name?: string; description?: string; scope?: string }) =>
    api<Role>(`/roles/${id}`, { method: 'PATCH', body }),
  savePermissions: (id: string, permissions: PermissionKey[]) =>
    api<{ role: Role; granted: PermissionKey[]; revoked: PermissionKey[] }>(
      `/roles/${id}/permissions`,
      { method: 'PUT', body: { permissions } },
    ),
  remove: (id: string) => api<{ status: 'ok' }>(`/roles/${id}`, { method: 'DELETE' }),
};

export const userEndpoints = {
  list: (params: {
    status?: string | undefined;
    q?: string | undefined;
    page?: number;
    pageSize?: number;
  }) => api<Paginated<OrganizationUser>>(`/users${queryString(params)}`),
  invite: (body: { email: string; displayName: string; roleId: string; expiresInDays?: number }) =>
    api<OrganizationUser>('/users/invitations', { method: 'POST', body }),
  update: (
    id: string,
    body: { displayName?: string; status?: 'active' | 'suspended'; roleIds?: string[] },
  ) => api<OrganizationUser>(`/users/${id}`, { method: 'PATCH', body }),
  unlinkGoogle: (id: string) =>
    api<{ status: 'ok' }>(`/users/${id}/identities`, { method: 'DELETE' }),
  ownIdentities: () => api<Items<LinkedIdentity>>('/me/identities'),
  unlinkOwnIdentity: (identityId: string) =>
    api<{ status: 'ok' }>(`/me/identities/${identityId}`, { method: 'DELETE' }),
};
